import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useContext } from 'react';
import { fetchOpenAIText } from '../utils/openaiClient';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import "./LessonWorkspace.css";
import BottomNavigationBar from '../components/BottomNavigationBar';
import EditorTabContent from '../components/LessonWorkspace/EditorTabContent';
import WhiteboardTabContent from '../components/LessonWorkspace/WhiteboardTabContent';
import DocTabContent from '../components/LessonWorkspace/DocTabContent';
import HomeworkTabContent from '../components/LessonWorkspace/HomeworkTabContent';
import { useLessonRealtime, subscribeReactions } from '../utils/realtime';
import { useHomeworkBanner } from '../hooks/useHomeworkBanner';
import { useHomeworkTabs } from '../hooks/useHomeworkTabs';
import { useRoundOverlays } from '../hooks/useRoundOverlays';
import { useSaveToast } from '../hooks/useSaveToast';
import { useToolbarState } from '../hooks/useToolbarState';
import { useVideoCallState } from '../hooks/useVideoCallState';
import VideoCallModal from '../components/VideoCallModal';
import DeviceSelectModal from '../components/VideoCall/DeviceSelectModal';
import { getSavedDeviceSelection, getAlwaysShowPreview } from '../utils/deviceStorage';
import { useCommentState } from '../hooks/useCommentState';
import { useEditorRegistry } from '../hooks/useEditorRegistry';
import { useSaveState } from '../hooks/useSaveState';
import { useLessonMeta } from '../hooks/useLessonMeta';
import { useTabState } from '../hooks/useTabState';
import { useContextMenu } from '../hooks/useContextMenu';
import WebsiteEmbed from '../components/WebsiteEmbed';
import CommandMenu from '../components/CommandMenu';
import { getStudentData } from '../assets/data';
import { useLessonLoader } from '../hooks/useLessonLoader';
import { useHomeworkContentLoader } from '../hooks/useHomeworkContentLoader';
import { useContentSaveHandlers } from '../hooks/useContentSaveHandlers';
import { useExerciseBoardInsertion } from '../hooks/useExerciseBoardInsertion';
import { useAddToHomeworkListener } from '../hooks/useAddToHomeworkListener';
import { useBannerUpload } from '../hooks/useBannerUpload';
import { useTabManagement } from '../hooks/useTabManagement';
import { useContainerActions } from '../hooks/useContainerActions';
import { useRealtimeSync } from '../hooks/useRealtimeSync';
import { useRoundProgressTicker } from '../hooks/useRoundProgressTicker';
import initialLessonBannerImage from '../assets/novas-banner-template.jpeg';
import { ActiveEditorContext } from "../context/ActiveEditorContext";
import { createPortal } from 'react-dom';
import ReactionOverlay from '../components/ReactionOverlay.jsx';
import DictionaryModal from '../components/DictionaryModal.jsx';
import StudentChatModal from '../components/StudentChatModal.jsx';
import { ExerciseSyncProvider } from '../context/ExerciseSyncContext';
import { getSelectedTextElement } from '../utils/lessonFormattingUtils';
import LessonTitleInput from '../components/LessonTitleInput';
import LessonToolbar from '../components/LessonToolbar';
import LessonBanner from '../components/LessonBanner';
import RoundOverlays from '../components/RoundOverlays';
import SaveChangesModal from '../components/LessonWorkspace/SaveChangesModal.jsx';
import DictVariantsModal from '../components/LessonWorkspace/DictVariantsModal.jsx';
import ReactionsFab from '../components/LessonWorkspace/ReactionsFab.jsx';
import LiveSessionPill from '../components/LessonWorkspace/LiveSessionPill.jsx';
import StudentStatusPill from '../components/LessonWorkspace/StudentStatusPill.jsx';
import SelectionContextMenu from '../components/LessonWorkspace/SelectionContextMenu.jsx';
import { useDictionaryLookup } from '../hooks/useDictionaryLookup';
import { useLessonSaveGuard } from '../hooks/useLessonSaveGuard';
import { useHomeworkSubmission } from '../hooks/useHomeworkSubmission';
import Tour from '../components/Tour/Tour';
import TourTrigger from '../components/Tour/TourTrigger';
import { LESSON_TOUR_STEPS } from '../components/Tour/tourSteps';

const LESSON_TOUR_SEEN_KEY = 'novas_tour_lesson_v1_seen';

function MiroCursor({ x, y, name, color }) {
  return (
    <div style={{ position: 'fixed', left: x, top: y, pointerEvents: 'none', zIndex: 10000 }}>
      <svg width="18" height="22" viewBox="0 0 18 22" fill="none" style={{ display: 'block', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}>
        <path d="M0 0L0 17L4 12.5L7 20L10 19L7 11.5L12 11.5Z" fill={color} stroke="white" strokeWidth="1.2" strokeLinejoin="round" />
      </svg>
      <div style={{
        position: 'absolute', left: 14, top: 12,
        background: color, color: '#fff',
        fontSize: 11, fontWeight: 600, lineHeight: '18px',
        padding: '0 7px', borderRadius: 20,
        whiteSpace: 'nowrap',
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        {name}
      </div>
    </div>
  );
}



export default function LessonWorkspace({ isStudent = false, lessonMode: defaultLessonMode = 'edit', onTabChange }) {
  const { activeEditor } = useContext(ActiveEditorContext);
  const [searchParams] = useSearchParams();
  const [tourOpen, setTourOpen] = useState(false);
  // The /lessons/new route is declared separately from /lessons/:lessonId
  // in App.js, so on /lessons/new useParams() returns {} and `lessonId`
  // comes back as undefined — NOT the literal string 'new'. Every check
  // in this file is written as `lessonId === 'new'`, so without this
  // normalisation those branches never trigger: the post-save navigate
  // that swaps the URL from /lessons/new to /lessons/{uuid} never fires,
  // the next auto-save still sees the route as "new", and INSERTs a
  // fresh lesson instead of UPDATEing the one we just created. Result:
  // one created student + one edit session = many duplicate lesson rows.
  const { lessonId: rawLessonId, studentSlug } = useParams();
  const lessonId = rawLessonId || 'new';

  // Auto-open lesson workspace tour on the teacher's first visit
  useEffect(() => {
    if (isStudent) return;
    try {
      if (!localStorage.getItem(LESSON_TOUR_SEEN_KEY)) {
        const t = setTimeout(() => setTourOpen(true), 900);
        return () => clearTimeout(t);
      }
    } catch (_) {}
  }, [isStudent]);
  const studentIdFromParams = searchParams.get('studentId');
  const homeworkId = searchParams.get('homeworkId'); // Получаем homeworkId из URL
  const navigate = useNavigate();
  
  // Для студентов используем studentSlug из URL, для учителей - из searchParams
  const studentId = isStudent ? studentSlug : studentIdFromParams;
  // Runtime lesson mode: allow read-only preview via URL (?mode=view)
  const urlMode = (searchParams.get('mode') || '').toLowerCase();
  const lessonMode = urlMode === 'view' ? 'view_only' : defaultLessonMode;
  

  
  // console.log('LessonWorkspace - lessonId:', lessonId);
  // console.log('LessonWorkspace - studentId:', studentId);
  
  const {
    lessonTitle, setLessonTitle,
    bannerImage, setBannerImage,
    docBanners, setDocBanners,
    homeworkBanners, setHomeworkBanners,
    editorContents, setEditorContents,
    whiteboardContents, setWhiteboardContents,
  } = useLessonMeta({ initialBannerImage: initialLessonBannerImage });
  // Realtime wiring: teacher publishes, student consumes
  const role = isStudent ? 'student' : 'teacher';
  // Identify current user so the other side sees a real name in the presence list
  const currentUser = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch { return {}; }
  }, []);
  const {
    remote,
    participants,
    publishTab,
    publishCursor,
    publishWhiteboardPatch,
    publishStudentWhiteboardPatch,
    publishController,
    publishSession,
    publishViewport,
    publishScroll,
    publishEditorContents,
    publishStudentEditorContents,
    publishReaction,
    publishStudentCursor,
    publishMediaState,
    publishStudentMediaState,
  } = useLessonRealtime(lessonId || 'new', role, {
    id: currentUser?.id || null,
    name: currentUser?.name || (isStudent ? 'Student' : 'Teacher'),
  });
  // Remote reactions — show incoming emoji bursts from the other side
  React.useEffect(() => {
    if (!lessonId) return;
    return subscribeReactions(lessonId, ({ emoji }) => {
      const idBase = `remote_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const { innerWidth: W, innerHeight: H } = window;
      const newOnes = Array.from({ length: 12 }).map((_, i) => ({
        id: `${idBase}_${i}`,
        emoji,
        x: Math.random() * (W - 40) + 20,
        y: Math.random() * (H - 120) + 60,
        delay: `${(i * 0.03).toFixed(2)}s`,
      }));
      setReactions(prev => [...prev, ...newOnes]);
      setTimeout(() => setReactions(prev => prev.filter(r => !r.id.startsWith(idBase))), 1800);
    });
  }, [lessonId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Follow Mode: if teacher has locked control, student is in read-only "watch" mode.
  const isFollowMode = remote?.controller === 'teacher';
  const isStudentLocked = isStudent && isFollowMode;
  // Session state (Variant D)
  const isLiveSession = !!remote?.sessionStartedAt;
  // Presence — who else is in the lesson (excluding ourselves where possible)
  const otherParticipants = (participants || []).filter(p => {
    if (currentUser?.id && p.id) return String(p.id) !== String(currentUser.id);
    return p.role !== role;
  });
  

  const {
    toast, setToast,
    videoOpen, setVideoOpen,
    studentCanJoinCall,
    reactions, setReactions,
    reactionsMenuOpen, setReactionsMenuOpen,
    dictionaryModalOpen, setDictionaryModalOpen,
    chatModalOpen, setChatModalOpen,
  } = useVideoCallState({ isStudent, lessonId });

  // Device check screen (camera/mic/speaker) shown before joining a call —
  // gates all three ways a call can be opened below. Skipped (going
  // straight to setVideoOpen) if the user previously unchecked "Always
  // show this preview when joining".
  const [devicePromptOpen, setDevicePromptOpen] = useState(false);
  const [pendingDeviceSelection, setPendingDeviceSelection] = useState(null);
  const deviceCheckConfirmRef = useRef(null);

  const openCallWithDeviceCheck = (onConfirmed) => {
    if (!getAlwaysShowPreview()) {
      setPendingDeviceSelection(getSavedDeviceSelection());
      setVideoOpen(true);
      if (onConfirmed) onConfirmed();
      return;
    }
    deviceCheckConfirmRef.current = onConfirmed || null;
    setDevicePromptOpen(true);
  };

  const handleDeviceCheckConfirm = (selection) => {
    setPendingDeviceSelection(selection);
    setDevicePromptOpen(false);
    setVideoOpen(true);
    const onConfirmed = deviceCheckConfirmRef.current;
    deviceCheckConfirmRef.current = null;
    if (onConfirmed) onConfirmed();
  };

  const handleDeviceCheckCancel = () => {
    setDevicePromptOpen(false);
    deviceCheckConfirmRef.current = null;
  };

  // Auto-open video call when student arrives via a Start Lesson link (?call=1)
  useEffect(() => {
    if (isStudent && searchParams.get('call') === '1') {
      openCallWithDeviceCheck();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dictionary "Add to Dictionary" variants modal state

  const initialHomeworkBannerImage = useHomeworkBanner();

  
  
  // Инициализируем тип таба по умолчанию - убрали, так как это конфликтует с логикой переключения
  
  const fileInputRef = useRef(null);
  const scrollPositionsRef = useRef({});

  const {
    whiteboards, setWhiteboards,
    currentTab, setCurrentTabState,
    whiteboardCursorPositions, setWhiteboardCursorPositions,
    teacherCursor, setTeacherCursor,
    studentCursor, setStudentCursor,
    prevTab, setPrevTab,
    containers, setContainers,
    docContainers, setDocContainers,
    homeworkContainers, setHomeworkContainers,
    homeworkDataCache, setHomeworkDataCache,
    sendHomeworkSubmittingId, setSendHomeworkSubmittingId,
    homeworkSuccessToast, setHomeworkSuccessToast,
    tabSwitchInProgressRef,
    processedHomeworkRef,
    lastHomeworkIdRef,
  } = useTabState();

  // Continuously save window scroll for doc/editor tabs so restoring is always accurate
  useEffect(() => {
    const isDoc = currentTab === 0 ||
      (currentTab > 0 && (whiteboards[currentTab - 1]?.type === 'doc' || whiteboards[currentTab - 1]?.type === 'homework'));
    if (!isDoc) return;
    const onScroll = () => {
      scrollPositionsRef.current[currentTab] = window.scrollY;
      console.log('[scroll-debug] saved scroll for tab', currentTab, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [currentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore scroll position when switching to a doc/editor tab.
  // useLayoutEffect fires before paint (no flash). ResizeObserver re-applies once
  // content finishes loading in production (document may be too short initially).
  const pendingScrollRef = useRef(null);

  useLayoutEffect(() => {
    const isDoc = currentTab === 0 ||
      (currentTab > 0 && (whiteboards[currentTab - 1]?.type === 'doc' || whiteboards[currentTab - 1]?.type === 'homework'));
    if (!isDoc) { pendingScrollRef.current = null; return; }
    const saved = scrollPositionsRef.current[currentTab];
    console.log('[scroll-debug] layoutEffect restore attempt for tab', currentTab, 'saved=', saved, 'maxScroll=', document.documentElement.scrollHeight - window.innerHeight);
    if (saved == null) { pendingScrollRef.current = null; return; }
    pendingScrollRef.current = saved;
    window.scrollTo({ top: saved, behavior: 'instant' });
  }, [currentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const target = pendingScrollRef.current;
    console.log('[scroll-debug] ResizeObserver effect armed, target=', target);
    if (target == null) return;
    // This observer exists to catch ONE case: content that's still loading
    // right after a tab switch, so the page is too short for `scrollTo` to
    // reach `target` yet. Without bounds, it stays armed indefinitely and
    // re-fires on ANY later resize of `document.body` — including the
    // reflow caused by normal typing (a paragraph growing by one line).
    // That yanked the scroll position back to the stale tab-switch target
    // while the user was actively composing text, most noticeably in
    // whichever container sits highest on the page (its growth changes
    // document height most directly). Bound the window to ~2s after the
    // tab switch, and skip firing while the user is actively editing.
    const armedAt = Date.now();
    const observer = new ResizeObserver(() => {
      if (Date.now() - armedAt > 2000) {
        console.log('[scroll-debug] ResizeObserver gave up after 2s, target was', target, 'maxScroll=', document.documentElement.scrollHeight - window.innerHeight);
        observer.disconnect();
        pendingScrollRef.current = null;
        return;
      }
      const activeEl = document.activeElement;
      const isTyping = !!(activeEl && activeEl.closest && activeEl.closest('.ProseMirror'));
      if (isTyping) { console.log('[scroll-debug] ResizeObserver fired but user is typing, skip'); return; }
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      console.log('[scroll-debug] ResizeObserver fired, maxScroll=', maxScroll, 'target=', target);
      if (maxScroll >= target - 10) {
        console.log('[scroll-debug] ResizeObserver applying scroll to', target);
        window.scrollTo({ top: target, behavior: 'instant' });
        pendingScrollRef.current = null;
        observer.disconnect();
      }
    });
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [currentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Обертка для setCurrentTab с вызовом onTabChange
  const setCurrentTab = useCallback((tabIndex) => {
    // Защита: не позволяем сбросить таб на 0, если пользователь находится на homework табе и не переключается вручную
    if (tabIndex === 0 && currentTab > 0 && whiteboards[currentTab - 1]?.type === 'homework' && !tabSwitchInProgressRef.current) {
      const currentHomeworkId = searchParams.get('homeworkId') || homeworkId;
      if (currentHomeworkId) {
        // Пользователь на homework табе, не сбрасываем на главный редактор автоматически
        console.log('Preventing automatic tab reset from homework tab to editor');
        return;
      }
    }
    
    setCurrentTabState(tabIndex);
    
    // Определяем тип таба и вызываем callback
    if (onTabChange) {
      let tabType = 'editor';
      if (tabIndex > 0 && whiteboards[tabIndex - 1]) {
        tabType = whiteboards[tabIndex - 1].type;
      }
      onTabChange(tabType);
    }
    // Publish tab changes for student mirror
    publishTab(tabIndex);
    try {
      const tabType = tabIndex > 0 && whiteboards[tabIndex - 1] ? whiteboards[tabIndex - 1].type : 'editor';
      document.body.setAttribute('data-novas-current-tab-type', tabType);
    } catch (_) {}
  // searchParams.get(...) result (a stable string/null), not the
  // searchParams object itself — useSearchParams() returns a new object
  // reference every render, so this recreated setCurrentTab on every
  // render, which in turn re-triggered every effect that lists
  // setCurrentTab as a dependency (including the homework-load effect
  // below) far more often than an actual homeworkId change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTabChange, whiteboards, publishTab, currentTab, searchParams.get('homeworkId'), homeworkId]);
  const {
    containerComments, setContainerComments,
    expandedCommentKey, setExpandedCommentKey,
    commentTextFor, setCommentTextFor,
    commentTextDraft, setCommentTextDraft,
    commentRecordingFor,
    commentUploading, setCommentUploading,
    mediaRecorderRef,
  } = useCommentState();
  const roundOverlaysState = useRoundOverlays();
  const {
    videoRoundState, setVideoRoundState,
    videoRoundContainerKey, setVideoRoundContainerKey,
    setVideoRoundBlob,
    setVideoRoundUrl,
    videoRoundStreamRef, videoRoundRecorderRef, videoRoundChunksRef, videoRoundVideoRef,
    videoRoundDragRef, videoRoundDidDragRef,
    insertedRoundVideos, setInsertedRoundVideos,
    insertedRoundHoveredId, setInsertedRoundHoveredId,
    audioRoundState, setAudioRoundState,
    audioRoundContainerKey, setAudioRoundContainerKey,
    setAudioRoundBlob,
    setAudioRoundUrl,
    setAudioRoundPosition,
    audioRoundStreamRef, audioRoundRecorderRef, audioRoundChunksRef,
    audioRoundDragRef, audioRoundDidDragRef,
    insertedRoundAudios, setInsertedRoundAudios,
    insertedRoundAudiosHoveredId, setInsertedRoundAudiosHoveredId,
    textRoundDragRef,
    insertedRoundTexts, setInsertedRoundTexts,
  } = roundOverlaysState;
  const homeworkContentRef = useRef(null);
  // Snapshot of the student's per-exercise state (answers, showResults,
  // score, etc.) pulled off the submitted homework row and handed to
  // ExerciseSyncProvider so it can hydrate its React state — the provider
  // otherwise only reads localStorage once on first mount, before this
  // async fetch resolves, so the teacher would see blank exercises.
  const [submittedExerciseStates, setSubmittedExerciseStates] = useState(null);
  useRoundProgressTicker({
    insertedRoundVideos, setInsertedRoundVideos,
    insertedRoundAudios, setInsertedRoundAudios,
  });
  // (moved below after all relevant state declarations)
  // const [draggedContainer, setDraggedContainer] = useState(null); // ОТКЛЮЧЕНО: drag and drop контейнеров
  const {
    mainEditor, setMainEditor,
    activeEditorId,
    allEditors,
    allEditorsRef,
    whiteboardsRef,
    setActiveEditorHandler,
    addEditorToMap,
  } = useEditorRegistry({ whiteboards });
  const { saveToast, showLessonSaveToast, dismissLessonSaveToast } = useSaveToast();
  const {
    saveStatus, setSaveStatus,
    isLoading, setIsLoading,
    showSaveChangesModal, setShowSaveChangesModal,
    pendingNavigation, setPendingNavigation,
  } = useSaveState();
  
  const {
    contextMenu, setContextMenu,
    translation, setTranslation,
    handleTextSelection,
    handleContextMenu,
    handleCloseContextMenu,
  } = useContextMenu();

  const {
    dictWordVariants,
    showDictVariantsModal,
    dictPendingWord,
    dictSelectedIndices,
    dictCurrentEntries,
    isDictVariantAlreadyAdded,
    closeDictVariantsModal,
    toggleDictVariantIndex,
    handleDictSelectSelected,
    handleDictSelectAll,
    handleAddToDictionary,
  } = useDictionaryLookup({ studentId, contextMenu, setContextMenu, showLessonSaveToast, dismissLessonSaveToast });


  useAddToHomeworkListener({
    currentTab, whiteboards, setWhiteboards, setHomeworkContainers,
    allEditorsRef, whiteboardsRef, setCurrentTab, homeworkContainers, allEditors,
  });

  // Function to get translation
  const getTranslation = async (text) => {
    try {
      // Определяем язык текста и направление перевода
      const isRussian = /[а-яё]/i.test(text);
      const targetLanguage = isRussian ? 'English' : 'Russian';
      const prompt = isRussian 
        ? `Translate this Russian text to English: "${text}". Return only the translation, no explanations.`
        : `Translate this text to Russian: "${text}". Return only the translation, no explanations.`;

      console.log('Translating:', text, 'to', targetLanguage);
      
      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.3,
        maxTokens: 150,
      });
      if (!content) throw new Error('Empty translation');
      return content.trim();
    } catch (error) {
      console.error('Translation error:', error);
      if (error.message.includes('401')) {
        return 'API key invalid';
      } else if (error.message.includes('429')) {
        return 'Rate limit exceeded';
      } else {
        return `Translation error: ${error.message}`;
      }
    }
  };

  const handleTranslate = async () => {
    if (contextMenu.selectedText) {
      setTranslation('Translating...');
      const translationResult = await getTranslation(contextMenu.selectedText);
      setTranslation(translationResult);
    }
  };

  
  // Получаем имя студента
  const studentName = studentId ? (() => {
    try {
      const studentData = getStudentData();
      const student = studentData[studentId];
      // Для студента в их собственном приложении getStudentData() пустой,
      // поэтому fallback на currentUser.name (обновляется при логине из students.name)
      return student?.name || (isStudent ? currentUser?.name || '' : '');
    } catch (e) {
      return isStudent ? currentUser?.name || '' : '';
    }
  })() : '';
  
  // State for whiteboard text and exercise creation functions
  const [whiteboardCreators, setWhiteboardCreators] = useState({});
  
  const {
    currentAlignment, setCurrentAlignment,
    currentLineSpacing, setCurrentLineSpacing,
    showColorPopup, setShowColorPopup,
    colorButtonRef, setColorButtonRef,
    showHighlightPopup, setShowHighlightPopup,
    highlightButtonRef, setHighlightButtonRef,
    fontSizeFocused, setFontSizeFocused,
    fontSizeDropdownOpen, setFontSizeDropdownOpen,
    fontFamilyDropdownOpen, setFontFamilyDropdownOpen,
    fontSelectHovered, setFontSelectHovered,
    fontSizeRef,
    fontSizeDropdownPos, setFontSizeDropdownPos,
    fontSizeHovered, setFontSizeHovered,
    fontFamilyDropdownPos, setFontFamilyDropdownPos,
    fontFamilyRef,
    hoveredButton, setHoveredButton,
    setForceUpdate,
  } = useToolbarState({ activeEditor, currentTab, whiteboards });

  const { handleInsertSmartLesson, insertExerciseToBoard } = useExerciseBoardInsertion({
    setContainers, setEditorContents, setWhiteboards, setHomeworkContainers,
    allEditorsRef, whiteboardsRef,
    currentTab, whiteboards, activeEditorId, allEditors, mainEditor,
    whiteboardCreators, setToast,
  });


  // Text selection handlers

  // --- Dictionary variants modal helpers ---

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Проверяем, что клик не на элементах тулбара или попапов
      const isToolbarClick = e.target.closest('.toolbar') || 
                           e.target.closest('[data-toolbar-element]') ||
                           e.target.closest('[style*="position: fixed"]') || // Попапы
                           e.target.closest('[style*="z-index: 1002"]') || // Выпадающие списки
                           colorButtonRef?.current?.contains(e.target) ||
                           highlightButtonRef?.current?.contains(e.target) ||
                           fontSizeRef?.current?.contains(e.target) ||
                           fontFamilyRef?.current?.contains(e.target);
      
      if (contextMenu.show) {
        handleCloseContextMenu();
      }
      if (translation) {
        setTranslation(null);
      }
      if (showColorPopup && !isToolbarClick) {
        setShowColorPopup(false);
      }
      if (showHighlightPopup && !isToolbarClick) {
        setShowHighlightPopup(false);
      }
      if (fontSizeDropdownOpen && !isToolbarClick) {
        setFontSizeDropdownOpen(false);
      }
      if (fontFamilyDropdownOpen && !isToolbarClick) {
        setFontFamilyDropdownOpen(false);
      }

    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu.show, translation, showColorPopup, showHighlightPopup, colorButtonRef, fontFamilyDropdownOpen, fontSizeDropdownOpen, highlightButtonRef]);

  // Text selection tracking
  useEffect(() => {
    const handleSelectionChange = () => {
      // Увеличиваем задержку чтобы убедиться что выделение полностью завершено
      setTimeout(handleTextSelection, 300);
      
      // Принудительно обновляем тулбар при изменении выделения
      setForceUpdate(prev => prev + 1);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('mouseup', handleSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mouseup', handleSelectionChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    getContainerContentChangeHandler,
    getHomeworkContainerContentChangeHandler,
    getWhiteboardContentChangeHandler,
    whiteboardHandlers,
  } = useContentSaveHandlers({
    setEditorContents, setWhiteboardContents, setWhiteboardCursorPositions,
    whiteboards, isStudent, publishWhiteboardPatch, publishStudentWhiteboardPatch,
    homeworkDataCache, setHomeworkDataCache, homeworkContainers, editorContents,
    allEditorsRef, studentId, lessonId,
  });

  useLessonLoader({
    lessonId, homeworkId, isStudent, lessonTitle, whiteboards,
    initialLessonBannerImage,
    setIsLoading, setLessonTitle, setDocBanners, setHomeworkBanners,
    setContainers, setDocContainers, setHomeworkContainers, setHomeworkDataCache,
    setWhiteboards, setEditorContents, setWhiteboardContents, setContainerComments,
    setInsertedRoundVideos, setInsertedRoundAudios, setInsertedRoundTexts,
    setBannerImage,
  });

  // Загружаем содержимое homework контейнеров при открытии homework из URL
  useHomeworkContentLoader({
    homeworkId, searchParams, isStudent, lessonId, studentId, currentUser,
    setWhiteboards, setHomeworkContainers, setEditorContents, setContainerComments,
    setHomeworkDataCache, setSubmittedExerciseStates,
  });

  // Отслеживаем изменения в содержимом (убираем спам в консоли)
  // useEffect(() => {
  //   console.log("Editor contents changed:", editorContents);
  // }, [editorContents]);

  // useEffect(() => {
  //   console.log("Whiteboard contents changed:", whiteboardContents);
  // }, [whiteboardContents]);


  // Перехватываем клики по ссылкам sidebar'а для показа modal'а сохранения
  useEffect(() => {
    const handleSidebarClick = (e) => {
      const sidebarLink = e.target.closest('.sidebar-link');
      if (sidebarLink && sidebarLink.getAttribute('href')) {
        e.preventDefault();
        e.stopPropagation();
        const href = sidebarLink.getAttribute('href');
        handleNavigationAttempt(() => navigate(href));
      }
    };

    document.addEventListener('click', handleSidebarClick, true);
    return () => {
      document.removeEventListener('click', handleSidebarClick, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const {
    addSnapshotToLibrary,
    setAddSnapshotToLibrary,
    handleSave,
    handleNavigationAttempt,
    handleSaveAndNavigate,
    handleNavigateWithoutSaving,
    handleCancelNavigation,
  } = useLessonSaveGuard({
    isStudent, lessonTitle, studentId, lessonId, mainEditor,
    editorContents, whiteboardContents, containers, docContainers, homeworkContainers,
    homeworkDataCache, setHomeworkDataCache, whiteboards, containerComments,
    insertedRoundVideos, insertedRoundAudios, insertedRoundTexts,
    bannerImage, docBanners, homeworkBanners,
    navigate, showLessonSaveToast, setSaveStatus, isLoading,
    setShowSaveChangesModal, pendingNavigation, setPendingNavigation,
    searchParams, homeworkId,
  });

  const {
    moveContainerUp, moveContainerDown,
    moveDocContainerUp, moveDocContainerDown,
    addContainer, addDocContainer,
    deleteContainer, deleteDocContainer,
    addHomeworkContainer, deleteHomeworkContainer,
    moveHomeworkContainerUp, moveHomeworkContainerDown,
  } = useContainerActions({
    setContainers, setDocContainers, setHomeworkContainers, setEditorContents,
  });

  const { handleMarkReviewed, handleSendHomework } = useHomeworkSubmission({
    isStudent, studentId, lessonId, currentUser,
    homeworkDataCache, setHomeworkDataCache,
    homeworkContainers, setHomeworkContainers,
    editorContents, setEditorContents,
    allEditorsRef, setWhiteboards, setContainerComments,
    setSendHomeworkSubmittingId, setHomeworkSuccessToast,
  });

  const handleTabChange = (index) => {
    setPrevTab(currentTab);
    // Отмечаем, что пользователь переключил таб вручную - это имеет приоритет над автоматическим переключением
    tabSwitchInProgressRef.current = true;
    setCurrentTab(index);
    // Сбрасываем состояние тулбара при переключении вкладок
    setShowColorPopup(false);
    setShowHighlightPopup(false);
    setFontSizeDropdownOpen(false);
    setFontFamilyDropdownOpen(false);

    // Сбрасываем флаг через задержку, чтобы предотвратить конфликты
    setTimeout(() => {
      tabSwitchInProgressRef.current = false;
    }, 500);
  };

  useRealtimeSync({
    isStudent, remote, currentUser, currentTab, isStudentLocked,
    setTeacherCursor, setWhiteboardContents, setWhiteboards, setStudentCursor,
    setEditorContents,
    publishCursor, publishStudentCursor, publishScroll,
    publishEditorContents, editorContents, publishStudentEditorContents,
    publishMediaState, publishStudentMediaState,
    handleTabChange,
  });

  const { addHomework } = useHomeworkTabs({
    currentTab, whiteboards, setWhiteboards,
    homeworkDataCache, setHomeworkDataCache,
    homeworkContainers, setHomeworkContainers,
    setEditorContents, setContainerComments,
    studentId, lessonId, homeworkId, searchParams,
    setCurrentTab, setLessonTitle,
    whiteboardsRef, processedHomeworkRef, tabSwitchInProgressRef, lastHomeworkIdRef,
    homeworkSuccessToast, setHomeworkSuccessToast,
    setHomeworkBanners,
  });

  const addWhiteboard = () => {
    const newId = Date.now(); // Используем timestamp для уникального ID
    // Очищаем localStorage для нового whiteboard'а чтобы избежать конфликтов
    localStorage.removeItem(`whiteboard_${newId}`);
    setWhiteboards([...whiteboards, { id: newId, type: 'whiteboard' }]);
    setCurrentTab(whiteboards.length + 1);
  };

  const addDoc = () => {
    const newId = Date.now(); // Используем timestamp для уникального ID
    setWhiteboards([...whiteboards, { id: newId, type: 'doc' }]);
    // Инициализируем пустые контейнеры для нового doc таба
    setDocContainers(prev => ({
      ...prev,
      [newId]: [] // Пустой массив - начинаем без контейнеров
    }));
    // Инициализируем базовый баннер для нового doc'а
    setDocBanners(prev => ({
      ...prev,
      [newId]: initialLessonBannerImage
    }));
    setCurrentTab(whiteboards.length + 1);
  };

  const addWebsite = () => {
    const newId = Date.now(); // Используем timestamp для уникального ID
    setWhiteboards([...whiteboards, { id: newId, type: 'website' }]);
    setCurrentTab(whiteboards.length + 1);
  };


  const { onDeleteTab, onReorderTabs } = useTabManagement({
    whiteboards, setWhiteboards,
    setDocContainers, setDocBanners,
    setHomeworkContainers, setHomeworkBanners, setHomeworkDataCache,
    currentTab, setCurrentTab, prevTab,
  });

  const renderContent = () => {
    // console.log('renderContent called, currentTab:', currentTab);
    // console.log('containers:', containers);
    // console.log('whiteboards:', whiteboards);
    
    if (currentTab === 0) {
      return (
        <EditorTabContent
          containers={containers}
          editorContents={editorContents}
          setMainEditor={setMainEditor}
          setActiveEditorHandler={setActiveEditorHandler}
          addEditorToMap={addEditorToMap}
          getContainerContentChangeHandler={getContainerContentChangeHandler}
          isStudent={isStudent}
          lessonMode={lessonMode}
          remote={remote}
          isFollowMode={isFollowMode}
          isStudentLocked={isStudentLocked}
          moveContainerUp={moveContainerUp}
          moveContainerDown={moveContainerDown}
          deleteContainer={deleteContainer}
          addContainer={addContainer}
        />
      );
    }

    const currentWhiteboard = whiteboards[currentTab - 1];
    // console.log('currentWhiteboard:', currentWhiteboard);
    if (currentWhiteboard?.type === 'whiteboard') {
      return (
        <WhiteboardTabContent
          currentWhiteboard={currentWhiteboard}
          whiteboardContents={whiteboardContents}
          whiteboardHandlers={whiteboardHandlers}
          isStudent={isStudent}
          remote={remote}
          publishStudentWhiteboardPatch={publishStudentWhiteboardPatch}
          publishWhiteboardPatch={publishWhiteboardPatch}
          isFollowMode={isFollowMode}
          publishViewport={publishViewport}
          setWhiteboardCreators={setWhiteboardCreators}
          currentTab={currentTab}
          lessonMode={lessonMode}
          currentUser={currentUser}
          lessonId={lessonId}
        />
      );
    }

    if (currentWhiteboard?.type === 'doc') {
      return (
        <DocTabContent
          currentWhiteboard={currentWhiteboard}
          whiteboardContents={whiteboardContents}
          docContainers={docContainers}
          editorContents={editorContents}
          setMainEditor={setMainEditor}
          setActiveEditorHandler={setActiveEditorHandler}
          addEditorToMap={addEditorToMap}
          setEditorContents={setEditorContents}
          isStudent={isStudent}
          lessonMode={lessonMode}
          remote={remote}
          isFollowMode={isFollowMode}
          isStudentLocked={isStudentLocked}
          moveDocContainerUp={moveDocContainerUp}
          moveDocContainerDown={moveDocContainerDown}
          deleteDocContainer={deleteDocContainer}
          addDocContainer={addDocContainer}
        />
      );
    }

    if (currentWhiteboard?.type === 'website') {
      const websiteContent = whiteboardContents[`whiteboard-${currentWhiteboard.id}`] || '';
      
      // console.log(`Rendering website ${currentWhiteboard.id} with content:`, websiteContent);
      
      return (
        <WebsiteEmbed 
          id={currentWhiteboard.id}
          initialContent={websiteContent}
          onContentChange={getWhiteboardContentChangeHandler(currentWhiteboard.id)}
        />
      );
    }

    if (currentWhiteboard?.type === 'homework') {
      return (
        <HomeworkTabContent
          currentWhiteboard={currentWhiteboard}
          whiteboards={whiteboards}
          setWhiteboards={setWhiteboards}
          whiteboardContents={whiteboardContents}
          homeworkDataCache={homeworkDataCache}
          setHomeworkDataCache={setHomeworkDataCache}
          homeworkContainers={homeworkContainers}
          setHomeworkContainers={setHomeworkContainers}
          editorContents={editorContents}
          setEditorContents={setEditorContents}
          containerComments={containerComments}
          setContainerComments={setContainerComments}
          isStudent={isStudent}
          lessonMode={lessonMode}
          remote={remote}
          setMainEditor={setMainEditor}
          setActiveEditorHandler={setActiveEditorHandler}
          addEditorToMap={addEditorToMap}
          getHomeworkContainerContentChangeHandler={getHomeworkContainerContentChangeHandler}
          moveHomeworkContainerUp={moveHomeworkContainerUp}
          moveHomeworkContainerDown={moveHomeworkContainerDown}
          deleteHomeworkContainer={deleteHomeworkContainer}
          addHomeworkContainer={addHomeworkContainer}
          handleSendHomework={handleSendHomework}
          handleMarkReviewed={handleMarkReviewed}
          sendHomeworkSubmittingId={sendHomeworkSubmittingId}
          homeworkContentRef={homeworkContentRef}
          expandedCommentKey={expandedCommentKey}
          setExpandedCommentKey={setExpandedCommentKey}
          commentTextFor={commentTextFor}
          setCommentTextFor={setCommentTextFor}
          commentTextDraft={commentTextDraft}
          setCommentTextDraft={setCommentTextDraft}
          commentRecordingFor={commentRecordingFor}
          commentUploading={commentUploading}
          mediaRecorderRef={mediaRecorderRef}
          insertedRoundVideos={insertedRoundVideos}
          setInsertedRoundVideos={setInsertedRoundVideos}
          insertedRoundHoveredId={insertedRoundHoveredId}
          setInsertedRoundHoveredId={setInsertedRoundHoveredId}
          insertedRoundAudios={insertedRoundAudios}
          setInsertedRoundAudios={setInsertedRoundAudios}
          insertedRoundAudiosHoveredId={insertedRoundAudiosHoveredId}
          setInsertedRoundAudiosHoveredId={setInsertedRoundAudiosHoveredId}
          insertedRoundTexts={insertedRoundTexts}
          setInsertedRoundTexts={setInsertedRoundTexts}
          videoRoundDragRef={videoRoundDragRef}
          videoRoundDidDragRef={videoRoundDidDragRef}
          audioRoundDragRef={audioRoundDragRef}
          audioRoundDidDragRef={audioRoundDidDragRef}
          textRoundDragRef={textRoundDragRef}
          audioRoundState={audioRoundState}
          setAudioRoundState={setAudioRoundState}
          audioRoundContainerKey={audioRoundContainerKey}
          setAudioRoundContainerKey={setAudioRoundContainerKey}
          audioRoundStreamRef={audioRoundStreamRef}
          audioRoundChunksRef={audioRoundChunksRef}
          audioRoundRecorderRef={audioRoundRecorderRef}
          setAudioRoundBlob={setAudioRoundBlob}
          setAudioRoundUrl={setAudioRoundUrl}
          setAudioRoundPosition={setAudioRoundPosition}
          videoRoundState={videoRoundState}
          setVideoRoundState={setVideoRoundState}
          videoRoundContainerKey={videoRoundContainerKey}
          setVideoRoundContainerKey={setVideoRoundContainerKey}
          videoRoundStreamRef={videoRoundStreamRef}
          videoRoundChunksRef={videoRoundChunksRef}
          videoRoundRecorderRef={videoRoundRecorderRef}
          videoRoundVideoRef={videoRoundVideoRef}
          setVideoRoundBlob={setVideoRoundBlob}
          setVideoRoundUrl={setVideoRoundUrl}
        />
      );
    }

    // console.log('No content to render');
    return null;
  };

  // --- MAIN RENDER ---
  // eslint-disable-next-line no-unused-vars
  const isEditor = currentTab === 0;
  // eslint-disable-next-line no-unused-vars
  const isPrevEditor = prevTab === 0;
  // eslint-disable-next-line no-unused-vars
  const isWhiteboard = currentTab > 0 && whiteboards[currentTab - 1]?.type === 'whiteboard';
  // eslint-disable-next-line no-unused-vars
  const isDoc = currentTab > 0 && whiteboards[currentTab - 1]?.type === 'doc';
  // eslint-disable-next-line no-unused-vars
  const isPrevWhiteboard = prevTab > 0 && whiteboards[prevTab - 1]?.type === 'whiteboard';
  // eslint-disable-next-line no-unused-vars
  const isPrevDoc = prevTab > 0 && whiteboards[prevTab - 1]?.type === 'doc';

  const { handleBannerUploadClick, handleBannerFileChange } = useBannerUpload({
    fileInputRef, currentTab, whiteboards, setBannerImage, lessonId,
    setSaveStatus, handleSave, setDocBanners, setHomeworkBanners,
  });

  // Функция для определения активного элемента в зависимости от текущей вкладки
  const getActiveElement = () => {
    if (window.selectedWhiteboardTextShape) {
      const s = window.selectedWhiteboardTextShape.shape;
      return {
        style: {
          fontFamily: s.font || 'Arial, sans-serif',
          fontSize: `${s.fontSize || 24}px`,
          fontWeight: s.bold ? 'bold' : 'normal',
          fontStyle: s.italic ? 'italic' : 'normal',
          textDecoration: [s.underline && 'underline', s.strikethrough && 'line-through'].filter(Boolean).join(' ') || 'none',
          textAlign: s.textAlign || 'left',
          color: s.color || '#222',
        },
      };
    }
    if (window.selectedImageTextElement) {
      return window.selectedImageTextElement;
    }
    if (currentTab === 0 || (currentTab > 0 && (whiteboards[currentTab - 1]?.type === 'doc' || whiteboards[currentTab - 1]?.type === 'homework'))) {
      return activeEditor;
    }
    if (currentTab > 0 && whiteboards[currentTab - 1]?.type === 'whiteboard') {
      return getSelectedTextElement();
    }
    return null;
  };

  return (
    <ExerciseSyncProvider lessonId={lessonId} studentId={isStudent ? (currentUser?.id || studentId) : studentId} role={role} seedStates={submittedExerciseStates}>
    <div
      className="lesson-workspace"
      onContextMenu={handleContextMenu}
    >
      <LiveSessionPill
        isStudent={isStudent}
        otherParticipants={otherParticipants}
        isLiveSession={isLiveSession}
        studentId={studentId}
        lessonId={lessonId}
        isFollowMode={isFollowMode}
        onToggleSession={() => {
          if (isLiveSession) {
            publishSession('end');
          } else {
            // Session is only published once the teacher actually
            // confirms their camera/mic on the device check screen — if
            // they cancel it, no session starts and no link is copied.
            openCallWithDeviceCheck(() => {
              publishSession('start');
              if (studentId && lessonId) {
                const link = `${window.location.origin}/student-dashboard/${studentId}/lesson/${lessonId}?call=1`;
                navigator.clipboard.writeText(link).then(() => {
                  showLessonSaveToast('success', 'Link copied — share it with your student', 2500);
                }).catch(() => {
                  showLessonSaveToast('info', `Link: ${link}`, 5000);
                });
              }
            });
          }
        }}
        onCopyLink={() => {
          const link = `${window.location.origin}/student-dashboard/${studentId}/lesson/${lessonId}?call=1`;
          navigator.clipboard.writeText(link).then(() => {
            showLessonSaveToast('success', 'Link copied — share it with your student', 2500);
          }).catch(() => { showLessonSaveToast('info', `Link: ${link}`, 5000); });
        }}
        onToggleFollowMode={() => publishController(isFollowMode ? 'free' : 'teacher')}
      />

      <StudentStatusPill
        isStudent={isStudent}
        otherParticipants={otherParticipants}
        isLiveSession={isLiveSession}
        isStudentLocked={isStudentLocked}
        studentCanJoinCall={studentCanJoinCall}
        videoOpen={videoOpen}
        onJoinCall={() => openCallWithDeviceCheck()}
      />

      {/* Interaction blocker for student in follow mode */}
      {isStudentLocked && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            background: 'transparent',
            cursor: 'not-allowed',
          }}
          onWheel={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onTouchMove={(e) => e.preventDefault()}
        />
      )}

      {isStudent && teacherCursor && (
        <MiroCursor x={teacherCursor.x} y={teacherCursor.y} name={teacherCursor.name || 'Teacher'} color="#ee5a24" />
      )}
      {!isStudent && studentCursor && (
        <MiroCursor x={studentCursor.x} y={studentCursor.y} name={studentCursor.name || 'Student'} color="#667eea" />
      )}
      {/* Hidden file input for banner upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleBannerFileChange}
        accept="image/*"
      />

      {/* Global Toolbar */}
      <LessonToolbar
        fontFamilyRef={fontFamilyRef}
        fontSizeRef={fontSizeRef}
        fontSelectHovered={fontSelectHovered}
        setFontSelectHovered={setFontSelectHovered}
        fontSizeHovered={fontSizeHovered}
        setFontSizeHovered={setFontSizeHovered}
        fontSizeFocused={fontSizeFocused}
        setFontSizeFocused={setFontSizeFocused}
        fontFamilyDropdownOpen={fontFamilyDropdownOpen}
        setFontFamilyDropdownOpen={setFontFamilyDropdownOpen}
        fontSizeDropdownOpen={fontSizeDropdownOpen}
        setFontSizeDropdownOpen={setFontSizeDropdownOpen}
        fontFamilyDropdownPos={fontFamilyDropdownPos}
        setFontFamilyDropdownPos={setFontFamilyDropdownPos}
        fontSizeDropdownPos={fontSizeDropdownPos}
        setFontSizeDropdownPos={setFontSizeDropdownPos}
        showColorPopup={showColorPopup}
        setShowColorPopup={setShowColorPopup}
        colorButtonRef={colorButtonRef}
        setColorButtonRef={setColorButtonRef}
        showHighlightPopup={showHighlightPopup}
        setShowHighlightPopup={setShowHighlightPopup}
        highlightButtonRef={highlightButtonRef}
        setHighlightButtonRef={setHighlightButtonRef}
        currentAlignment={currentAlignment}
        setCurrentAlignment={setCurrentAlignment}
        currentLineSpacing={currentLineSpacing}
        setCurrentLineSpacing={setCurrentLineSpacing}
        activeEditor={activeEditor}
        getActiveElement={getActiveElement}
      />




      {/* Lesson Banner */}
      <LessonBanner
        currentTab={currentTab}
        whiteboards={whiteboards}
        bannerImage={bannerImage}
        docBanners={docBanners}
        homeworkBanners={homeworkBanners}
        initialHomeworkBannerImage={initialHomeworkBannerImage}
        studentId={studentId}
        isStudent={isStudent}
        fromHomework={searchParams.get('fromHomework') === 'true'}
        studentSlug={studentSlug}
        handleNavigationAttempt={handleNavigationAttempt}
        navigate={navigate}
        saveStatus={saveStatus}
        lessonTitle={lessonTitle}
        handleSave={handleSave}
        handleBannerUploadClick={handleBannerUploadClick}
      />

      {/* Device check screen — gates opening the call below via openCallWithDeviceCheck() */}
      <DeviceSelectModal
        isOpen={devicePromptOpen}
        onConfirm={handleDeviceCheckConfirm}
        onCancel={handleDeviceCheckCancel}
        initialDeviceIds={pendingDeviceSelection || getSavedDeviceSelection()}
      />

      {/* Video call — rendered at workspace level so it persists on all tabs */}
      {videoOpen && (
        <VideoCallModal
          isOpen={videoOpen}
          onClose={() => setVideoOpen(false)}
          lessonId={lessonId || 'new'}
          isStudent={isStudent}
          autoStart={!isStudent}
          autoJoin={isStudent}
          initialVideoDeviceId={pendingDeviceSelection?.videoDeviceId}
          initialAudioDeviceId={pendingDeviceSelection?.audioDeviceId}
          initialAudioOutputDeviceId={pendingDeviceSelection?.audioOutputDeviceId}
        />
      )}


      {/* Lesson Title Input Below Banner - показываем только для главного редактора, doc'ов и homework */}
      {(currentTab === 0 || (currentTab > 0 && (whiteboards[currentTab - 1]?.type === 'doc' || whiteboards[currentTab - 1]?.type === 'homework'))) && (
      <div className="lesson-title-section" data-tour="lesson-title">
        <LessonTitleInput
          value={lessonTitle}
          onChange={setLessonTitle}
        />
      </div>
      )}

      {saveToast && createPortal(
        <div
          className={`lesson-save-toast lesson-save-toast--${saveToast.variant}`}
          role="status"
          aria-live="polite"
        >
          <div className="lesson-save-toast-inner">
            <span className="lesson-save-toast-icon" aria-hidden>
              {saveToast.variant === 'success' ? '✓' : 'ℹ'}
            </span>
            <p className="lesson-save-toast-message">{saveToast.message}</p>
            <button
              type="button"
              className="lesson-save-toast-close"
              onClick={dismissLessonSaveToast}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>,
        document.body
      )}

      <SelectionContextMenu
        contextMenu={contextMenu}
        hoveredButton={hoveredButton}
        onHoverButton={setHoveredButton}
        onAddToDictionary={handleAddToDictionary}
        onTranslate={handleTranslate}
        translation={translation}
      />


      {/* Toast */}
      {toast && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: '#4CAF50',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10001,
            fontSize: '14px'
          }}
        >
          {toast}
        </div>
      )}

      {!isStudent && (
        <CommandMenu
          editor={activeEditor}
          cursorPosition={whiteboardCursorPositions[whiteboards[currentTab - 1]?.id] || { x: 0, y: 0 }}
          onInsertToBoard={insertExerciseToBoard}
          onInsertSmartLesson={handleInsertSmartLesson}
          currentTab={currentTab}
        />
      )}
      {isLoading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading lesson...
        </div>
      ) : (
        renderContent()
      )}
      {/* Показываем BottomNavigationBar для всех, но с разными пропсами */}
      <BottomNavigationBar
        whiteboards={whiteboards}
        currentTab={currentTab}
        setCurrentTab={handleTabChange}
        addWhiteboard={isStudent ? null : addWhiteboard}
        addDoc={isStudent ? null : addDoc}
        addWebsite={isStudent ? null : addWebsite}
        addHomework={isStudent ? null : addHomework}
        onDeleteTab={isStudent ? null : onDeleteTab}
        onReorderTabs={isStudent ? null : onReorderTabs}
        isStudent={isStudent}
      />


      {/* Save Changes Confirmation Modal */}
      <SaveChangesModal
        show={showSaveChangesModal}
        isStudent={isStudent}
        addSnapshotToLibrary={addSnapshotToLibrary}
        onAddSnapshotToLibraryChange={setAddSnapshotToLibrary}
        onCancel={handleCancelNavigation}
        onDontSave={handleNavigateWithoutSaving}
        onSaveAndContinue={handleSaveAndNavigate}
      />


      <ReactionOverlay reactions={reactions} />

      {/* Round comment overlays */}
      <RoundOverlays
        {...roundOverlaysState}
        commentUploading={commentUploading}
        setCommentUploading={setCommentUploading}
        setContainerComments={setContainerComments}
        setExpandedCommentKey={setExpandedCommentKey}
        homeworkContentRef={homeworkContentRef}
        lessonId={lessonId}
      />

      <DictionaryModal
        isOpen={dictionaryModalOpen}
        onClose={() => setDictionaryModalOpen(false)}
        studentId={studentId}
      />

      {/* Dictionary word variants selection modal (text-selection "Add to Dictionary") */}
      <DictVariantsModal
        show={showDictVariantsModal}
        pendingWord={dictPendingWord}
        variants={dictWordVariants}
        currentEntries={dictCurrentEntries}
        selectedIndices={dictSelectedIndices}
        isVariantAlreadyAdded={isDictVariantAlreadyAdded}
        onToggleVariant={toggleDictVariantIndex}
        onClose={closeDictVariantsModal}
        onSelectSelected={handleDictSelectSelected}
        onSelectAll={handleDictSelectAll}
      />


      <StudentChatModal
        isOpen={chatModalOpen}
        onClose={() => setChatModalOpen(false)}
        studentId={studentId}
        studentName={studentName}
        senderName={currentUser?.name || ''}
        isStudent={isStudent}
      />

      <ReactionsFab
        studentId={studentId}
        reactionsMenuOpen={reactionsMenuOpen}
        onOpenDictionary={() => {
          setReactionsMenuOpen(false);
          setChatModalOpen(false);
          setDictionaryModalOpen(true);
        }}
        onOpenChat={() => {
          setReactionsMenuOpen(false);
          setDictionaryModalOpen(false);
          setChatModalOpen(true);
        }}
        onToggleReactionsMenu={() => {
          setDictionaryModalOpen(false);
          setChatModalOpen(false);
          setReactionsMenuOpen(!reactionsMenuOpen);
        }}
        onCloseReactionsMenu={() => setReactionsMenuOpen(false)}
        onSendReaction={(emoji) => {
          const idBase = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
          const bursts = 12;
          const { innerWidth: W, innerHeight: H } = window;
          const newOnes = Array.from({ length: bursts }).map((_, i) => {
            const x = Math.random() * (W - 40) + 20;
            const y = Math.random() * (H - 120) + 60;
            return { id: `${idBase}_${i}`, emoji, x, y, delay: `${(i * 0.03).toFixed(2)}s` };
          });
          setReactions(prev => [...prev, ...newOnes]);
          setTimeout(() => setReactions(prev => prev.filter(r => !r.id.startsWith(idBase))), 1800);
          publishReaction(emoji);
          // Не закрываем панель при выборе реакции - она закроется при клике вне меню
        }}
      />

      {/* Плашка об успешной отправке ДЗ */}
      {homeworkSuccessToast && (
        <div className="homework-success-toast" role="status" aria-live="polite">
          <div className="homework-success-toast-inner">
            <span className="homework-success-toast-icon" aria-hidden>✓</span>
            <p className="homework-success-toast-message">{homeworkSuccessToast.message}</p>
            <button
              type="button"
              className="homework-success-toast-close"
              onClick={() => setHomeworkSuccessToast(null)}
              aria-label="Close"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {!isStudent && (
        <>
          {/* Tour button only on the main editor tab — whiteboards, website
              embeds and homework tabs hide it to keep their UI uncluttered. */}
          {currentTab === 0 && (
            <TourTrigger
              onClick={() => setTourOpen(true)}
              pulsing={!tourOpen && (() => { try { return !localStorage.getItem(LESSON_TOUR_SEEN_KEY); } catch (_) { return false; } })()}
              label="Tour"
              tall
            />
          )}
          <Tour
            steps={LESSON_TOUR_STEPS}
            isOpen={tourOpen}
            onClose={() => {
              setTourOpen(false);
              try { localStorage.setItem(LESSON_TOUR_SEEN_KEY, '1'); } catch (_) {}
            }}
            onComplete={() => {
              try { localStorage.setItem(LESSON_TOUR_SEEN_KEY, '1'); } catch (_) {}
            }}
          />
        </>
      )}
    </div>
    </ExerciseSyncProvider>
  );
}